import { DownloadOutlined, LockOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, DatePicker, Input, Modal, Select, Space, Statistic, Table, Tag, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { extractErrorMessage, parseFilename, triggerDownload } from 'pages/attendance-manager/component/export-all-modal';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import CreatorDrawer from './components/creator-drawer';
import NoAccess from './components/no-access';
import { MonthResponse, RewardRow, RowFilters } from './types';
import {
  DEFAULT_FILTERS, STATUS_COLOR, STATUS_LABEL, currentUtcMonth, filterRows, groupOptions, previousUtcMonth,
  hoursText, monthLabel, num, rewardErrorMessage, vnd, warningTexts,
} from './utils';
import styles from './style.module.scss';

const TITLE = 'Bảng thưởng tháng';
const isMonth = (v: string | null): v is string => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

/** Thưởng Online → Bảng thưởng tháng (docs/thuong-online/02-dac-ta-bang-data-creator.md §7). */
const OnlineRewardPage = () => {
  const { loaded, isAdmin, canViewOnlineReward } = useCurrentRoles();
  const [params, setParams] = useSearchParams();
  const month = isMonth(params.get('month')) ? (params.get('month') as string) : previousUtcMonth();
  const uid = params.get('uid');
  const [data, setData] = useState<MonthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<RowFilters>(DEFAULT_FILTERS);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    AxiosUtils.get(`${API.onlineRewardMonths}/${month}`)
      .then((res: any) => setData(res?.data ?? null))
      .catch((err) => {
        setData(null);
        setError(rewardErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    if (canViewOnlineReward) load();
  }, [canViewOnlineReward, load]);

  const rows = useMemo(() => filterRows(data?.rows ?? [], filters), [data, filters]);

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const response: any = await AxiosUtils.get(`${API.onlineRewardMonths}/${month}/export`, undefined, undefined, {
        responseType: 'blob',
        timeout: 120_000,
      });
      const filename = parseFilename(response?.headers?.['content-disposition'], `thuong-online-${month}.xlsx`);
      triggerDownload(
        new Blob([response.data], {
          type: response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        filename
      );
    } catch (err) {
      notification.error({ message: 'Không xuất được file', description: await extractErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const close = (force: boolean) => {
    setBusy(true);
    AxiosUtils.post(`${API.onlineRewardMonths}/${month}/close`, { force })
      .then((res: any) => {
        setData(res?.data ?? null);
        notification.success({ message: `Đã chốt tháng ${monthLabel(month)}` });
      })
      .catch((err: any) => {
        if (!force && err?.code === 'ONLINE_REWARD_CLOSE_NEEDS_CONFIRM') {
          Modal.confirm({ title: 'Vẫn chốt tháng?', content: err.message, okText: 'Vẫn chốt', cancelText: 'Để sau', onOk: () => close(true) });
          return;
        }
        notification.error({ message: 'Chưa chốt được', description: rewardErrorMessage(err) });
      })
      .finally(() => setBusy(false));
  };

  const askClose = () =>
    Modal.confirm({
      title: `Chốt tháng ${monthLabel(month)}?`,
      content: `${num(data?.counts.creators)} Creator · tổng ${vnd(data?.totals.reward)}. Sau khi chốt, số không đổi nữa; muốn sửa phải mở lại tháng.`,
      okText: 'Chốt tháng',
      cancelText: 'Huỷ',
      onOk: () => close(false),
    });

  const askReopen = () => {
    let reason = '';
    Modal.confirm({
      title: `Mở lại tháng ${monthLabel(month)}?`,
      content: <Input.TextArea rows={3} placeholder="Lý do mở lại (bắt buộc)" onChange={(e) => (reason = e.target.value)} />,
      okText: 'Mở lại',
      cancelText: 'Huỷ',
      onOk: () =>
        AxiosUtils.post(`${API.onlineRewardMonths}/${month}/reopen`, { reason })
          .then((res: any) => {
            setData(res?.data ?? null);
            notification.success({ message: 'Đã mở lại tháng' });
          })
          .catch((err) => {
            notification.error({ message: 'Chưa mở lại được', description: rewardErrorMessage(err) });
            return Promise.reject(err);
          }),
    });
  };

  if (loaded && !canViewOnlineReward) return <NoAccess />;

  const money = (v: number) => <span className={styles.money}>{vnd(v)}</span>;
  const columns: ColumnsType<RewardRow> = [
    {
      title: 'Creator', key: 'creator', fixed: 'left', width: 200,
      render: (_, r) => (
        <div className={styles.creator}>
          <strong>@{r.tiktokUsername || r.uid}</strong>
          {r.userName && <span className={styles.linked}>{r.userName} · đã gắn app</span>}
        </div>
      ),
    },
    { title: 'Nhóm', dataIndex: 'groupName', width: 170, ellipsis: true },
    { title: 'Ngày', dataIndex: 'liveDays', width: 70, align: 'right', sorter: (a, b) => a.liveDays - b.liveDays },
    { title: 'Giờ', key: 'hours', width: 90, align: 'right', render: (_, r) => hoursText(r.liveSeconds), sorter: (a, b) => a.liveSeconds - b.liveSeconds },
    { title: 'KC', dataIndex: 'kc', width: 120, align: 'right', render: (v) => <span className={styles.money}>{num(v)}</span>, sorter: (a, b) => a.kc - b.kc },
    { title: 'Thu nhập tự rút', dataIndex: 'incomeVnd', width: 140, align: 'right', render: money },
    {
      title: 'Bậc', key: 'level', width: 150,
      render: (_, r) => (r.level ? <span>Bậc {r.level} <Tag color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Tag></span> : <Tag>Chưa đạt</Tag>),
    },
    { title: 'So với', key: 'ref', width: 130, render: (_, r) => (r.referenceMonth ? `Bậc ${r.referenceLevel} · ${monthLabel(r.referenceMonth)}` : '—') },
    { title: 'NV A', dataIndex: 'a', width: 120, align: 'right', render: money },
    { title: 'NV B', dataIndex: 'b', width: 110, align: 'right', render: money },
    { title: 'NV C', dataIndex: 'c', width: 110, align: 'right', render: money },
    { title: 'NV D', dataIndex: 'd', width: 110, align: 'right', render: money },
    {
      title: 'Khoản tay', key: 'manual', width: 150, align: 'right',
      render: (_, r) => (
        <span className={styles.money}>
          {vnd(r.manual)} {r.pendingItems ? <Tag color="gold">{r.pendingItems} chờ</Tag> : null}
        </span>
      ),
    },
    {
      title: 'Tổng', dataIndex: 'total', width: 140, align: 'right', fixed: 'right', defaultSortOrder: 'descend', sorter: (a, b) => a.total - b.total,
      render: (_, r) => (r.vpnt ? <Tag color="red">VPNT · 0 đ</Tag> : <strong className={styles.money}>{vnd(r.total)}</strong>),
    },
  ];

  const warnings = data ? warningTexts(data) : [];
  return (
    <>
      <PageTitle
        title={TITLE}
        extra={
          <Space wrap>
            <DatePicker
              picker="month"
              allowClear={false}
              format="MM/YYYY"
              value={dayjs(`${month}-01`)}
              disabledDate={(d) => d.format('YYYY-MM') > currentUtcMonth()}
              onChange={(d) => d && setParam('month', d.format('YYYY-MM'))}
            />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Tải lại</Button>
            <Button icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!data} loading={busy}>Xuất Excel</Button>
            {isAdmin && data?.status === 'OPEN' && month < currentUtcMonth() && (
              <Button type="primary" icon={<LockOutlined />} onClick={askClose} loading={busy}>Chốt tháng</Button>
            )}
            {isAdmin && data?.status === 'CLOSED' && (
              <Button icon={<UnlockOutlined />} onClick={askReopen} loading={busy}>Mở lại</Button>
            )}
          </Space>
        }
      />
      {error && <Alert className={styles.section} type="error" showIcon message="Chưa tải được bảng thưởng" description={error} />}
      {data && (
        <>
          <div className={styles.statusLine}>
            {data.status === 'CLOSED' ? (
              <Tag color="green">Đã chốt v{data.closing?.version} · {data.closing?.closedAt ? dayjs(data.closing.closedAt).format('DD/MM/YYYY HH:mm') : ''} · {data.closing?.closedByName || 'Admin'}</Tag>
            ) : (
              <Tag color="blue">Tạm tính</Tag>
            )}
            <span>Chính sách {data.policy.id ? `#${data.policy.id}` : 'mặc định'} · hiệu lực từ {monthLabel(data.policy.effectiveMonth)}</span>
            {data.warnings.lastDataDay && <span>Dữ liệu tới ngày {data.warnings.lastDataDay.slice(8)}/{data.warnings.lastDataDay.slice(5, 7)}</span>}
            <span>{data.counts.linked} dòng đã gắn tài khoản app</span>
          </div>
          {warnings.length > 0 && (
            <Alert className={styles.warnings} type="warning" showIcon message="Lưu ý dữ liệu" description={<ul>{warnings.map((w) => <li key={w}>{w}</li>)}</ul>} />
          )}
          <div className={styles.tiles}>
            <div className={styles.tile}><Statistic title="Creator" value={data.counts.creators} /></div>
            <div className={styles.tile}><Statistic title="Có thưởng" value={data.counts.withReward} /></div>
            <div className={styles.tile}><Statistic title="Tổng thưởng" value={vnd(data.totals.reward)} /></div>
            <div className={styles.tile}><Statistic title="Tổng KC" value={num(data.totals.kc)} /></div>
            <div className={styles.tile}><Statistic title="Khoản chờ duyệt" value={data.counts.pendingItems} /></div>
          </div>
          <div className={styles.filters}>
            <Input.Search allowClear placeholder="Tìm @TikTok ID, UID, tên" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} style={{ width: 240 }} />
            <Select allowClear showSearch placeholder="Nhóm" value={filters.group || undefined} onChange={(v) => setFilters({ ...filters, group: v || '' })} options={groupOptions(data.rows)} style={{ width: 220 }} />
            <Select
              allowClear
              placeholder="Trạng thái bậc"
              value={filters.status || undefined}
              onChange={(v) => setFilters({ ...filters, status: v || '' })}
              options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
              style={{ width: 170 }}
            />
            <Checkbox checked={filters.onlyKc} onChange={(e) => setFilters({ ...filters, onlyKc: e.target.checked })}>Chỉ người có KC</Checkbox>
            <Checkbox checked={filters.onlyPending} onChange={(e) => setFilters({ ...filters, onlyPending: e.target.checked })}>Có khoản chờ duyệt</Checkbox>
            <Checkbox checked={filters.onlyVpnt} onChange={(e) => setFilters({ ...filters, onlyVpnt: e.target.checked })}>VPNT</Checkbox>
          </div>
          <Table
            rowKey="uid"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1800 }}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} dòng` }}
            onRow={(r) => ({ onClick: () => setParam('uid', r.uid), className: `${styles.clickable} ${r.vpnt ? styles.vpntRow : ''}` })}
          />
        </>
      )}
      <CreatorDrawer month={month} uid={uid} onClose={() => setParam('uid', null)} />
    </>
  );
};

export default OnlineRewardPage;
